import * as React from 'react';
import { storyblokEditable, type SbBlokData } from '@storyblok/react';

import type { HeroStoryblok, StoryblokRichtext } from '../../types/storyblok';
import { SiteContainer } from '../layout/SiteContainer';
import { StatCards } from '../ui/StatCards';

// Import decorative assets - using actual Figma exports
import backgroundSrc from '../../images/hero/background.svg';
import globeLeftSrc from '../../images/hero/globe-left-new.png';
import globeRightSrc from '../../images/hero/globe-right-new.png';
import ornamentSrc from '../../images/hero/ornament-new.svg';
import waveMaskSrc from '../../images/hero/wave-mask-new.svg';

interface HeroProps {
  blok: HeroStoryblok;
}

/**
 * HeroBackground renders the background elements matching Figma design.
 *
 * Mobile: Simple gradient background (brand-blue to brand-deep), height 612px
 * Desktop: Full decorative treatment with gradient, globes, ornament, and wave mask
 *
 * All decorative images are marked with alt="" and aria-hidden
 * for accessibility compliance (Requirement 6.2).
 */
const HeroBackground: React.FC = () => {
  return (
    <>
      {/* Mobile background - 612px gradient; stat cards (in flow below CTA) straddle its bottom edge */}
      <div
        className="absolute inset-x-0 top-0 h-[612px] bg-gradient-to-b from-brand-blue to-brand-deep desktop:hidden"
        aria-hidden="true"
      />

      {/* Desktop background - full decorative treatment */}
      <div className="hidden desktop:block" aria-hidden="true">
        {/* SVG Background with gradient */}
        <img
          src={backgroundSrc}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover pointer-events-none select-none"
        />

        {/* Right globe - large, anchored to right edge of 1440 design canvas (not viewport) */}
        <img
          src={globeRightSrc}
          alt=""
          aria-hidden="true"
          className="absolute -top-[10px] w-[1150px] h-[1150px] opacity-[0.24] pointer-events-none select-none left-[calc(50%+380px)]"
          style={{ transform: 'rotate(17.41deg)' }}
        />

        {/* Left globe - smaller, positioned at left edge */}
        <img
          src={globeLeftSrc}
          alt=""
          aria-hidden="true"
          className="absolute -left-[335px] top-[400px] w-[584px] h-[584px] opacity-[0.24] pointer-events-none select-none"
          style={{ transform: 'rotate(22.85deg)' }}
        />

        {/* Ornament - positioned in upper area with rotation */}
        <img
          src={ornamentSrc}
          alt=""
          aria-hidden="true"
          className="absolute left-[525px] top-[133px] w-[370px] h-[303px] pointer-events-none select-none"
          style={{ transform: 'rotate(-119.49deg)' }}
        />

        {/* Wave mask - positioned at specific Y coordinate */}
        <img
          src={waveMaskSrc}
          alt=""
          aria-hidden="true"
          className="absolute left-0 w-full pointer-events-none select-none"
          style={{ top: '769px', height: '263px' }}
        />
      </div>
    </>
  );
};

/**
 * HeroBackgroundSecondary renders the background for inner pages (secondary variant).
 *
 * Mobile: Simple gradient background, height driven by content
 * Desktop: Gradient with flipped wave (no globe/dot decorations)
 *
 * Key differences from primary:
 * - Wave is horizontally flipped
 * - Ornament dots decoration positioned far right
 * - No stat cards
 * - Height determined by content
 */
const HeroBackgroundSecondary: React.FC = () => {
  return (
    <>
      {/* Mobile background - gradient only */}
      <div
        className="absolute inset-0 bg-gradient-to-b from-brand-blue to-brand-deep desktop:hidden"
        aria-hidden="true"
      />

      {/* Desktop background - gradient with flipped wave and ornament dots */}
      <div className="hidden desktop:block absolute inset-0" aria-hidden="true">
        {/* SVG Background with gradient - positioned at top to hide lower wave */}
        <img
          src={backgroundSrc}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover object-top pointer-events-none select-none"
        />

        {/* Ornament dots - decorative scattered dots, positioned far right */}
        <img
          src={ornamentSrc}
          alt=""
          aria-hidden="true"
          className="absolute right-[80px] top-[80px] w-[370px] h-[303px] pointer-events-none select-none"
          style={{ transform: 'rotate(-119.49deg)' }}
        />

        {/* Wave mask - flipped horizontally, pushed down to show only top curve */}
        <img
          src={waveMaskSrc}
          alt=""
          aria-hidden="true"
          className="absolute left-0 w-full pointer-events-none select-none"
          style={{ height: '263px', bottom: '-190px', transform: 'scaleX(-1)' }}
        />
      </div>
    </>
  );
};

type HeroVariant = 'default' | 'secondary';

interface HeroContentProps {
  blok: HeroStoryblok;
  variant?: HeroVariant;
}

/**
 * Renders richtext headline with support for:
 * - Bold text rendered with accent color (secondary-light-sky)
 * - Line breaks (hard_break nodes)
 * - Paragraphs with line breaks between them
 */
const renderHeadline = (richtext: HeroStoryblok['headline']): React.ReactNode => {
  if (!richtext || !richtext.content) return null;

  const renderTextNode = (node: StoryblokRichtext, index: number): React.ReactNode => {
    if (node.type === 'text') {
      // Check for bold mark - render with accent color
      if (node.marks?.some((mark) => mark.type === 'bold')) {
        return (
          <span key={index} className="font-extrabold text-secondary-light-sky">
            {node.text}
          </span>
        );
      }
      return (
        <span key={index} className="font-semibold text-neutral-white">
          {node.text}
        </span>
      );
    }

    if (node.type === 'hard_break') {
      return <br key={index} />;
    }

    return null;
  };

  const paragraphs = richtext.content.filter((node) => node.type === 'paragraph');

  return paragraphs.map((paragraph, pIndex) => (
    <React.Fragment key={pIndex}>
      {paragraph.content?.map((child, i) => renderTextNode(child, i))}
      {pIndex < paragraphs.length - 1 && <br />}
    </React.Fragment>
  ));
};

/**
 * HeroContent renders the headline, subtitle, and CTA.
 *
 * Headline uses richtext with bold marks for accent color highlighting.
 * Line breaks in richtext are preserved.
 * Headline renders as semantic h1 element (Requirement 6.1).
 */
const HeroContent: React.FC<HeroContentProps> = ({ blok, variant = 'default' }) => {
  const hasCta = Boolean(blok.cta_label && blok.cta_url);

  // Different padding for variants:
  // - default: large top padding for homepage hero with stat cards
  // - secondary: smaller padding for inner page heroes, bottom padding positions wave apex
  const paddingClasses =
    variant === 'secondary'
      ? 'pt-[166px] pb-[140px] desktop:pt-[180px] desktop:pb-[160px]'
      : 'pt-[166px] desktop:pt-[257px]';

  return (
    <div
      className={`relative z-10 flex flex-col items-center gap-8 max-w-[804px] mx-auto text-center ${paddingClasses}`}
    >
      {/* Large logo - only shown on Home page (default variant) when hero_logo is set */}
      {variant === 'default' && blok.hero_logo?.filename && (
        <img
          src={blok.hero_logo.filename}
          alt={blok.hero_logo.alt || 'Global Mobility Alliance'}
          className="w-[200px] h-auto desktop:w-[296px]"
        />
      )}

      {/* Text content section */}
      <div className="flex flex-col gap-6 w-full">
        {/* Eyebrow - small uppercase label above headline */}
        {blok.eyebrow && (
          <span className="text-sm font-semibold uppercase tracking-widest text-secondary-light-sky">
            {blok.eyebrow}
          </span>
        )}

        {/* Headline - h1 for accessibility (Req 6.1)
            40px on all breakpoints per Figma design
            Uses Plus Jakarta Sans (font-primary) with normal line-height
            Bold text in richtext renders with accent color */}
        {blok.headline && (
          <h1 className="font-primary text-[40px] leading-normal">
            {renderHeadline(blok.headline)}
          </h1>
        )}

        {/* Subtitle - body2 text style (Req 2.6) */}
        {blok.subtitle && (
          <p className="text-body2 text-neutral-white font-normal">{blok.subtitle}</p>
        )}
      </div>

      {/* CTA button (Req 1.3, 1.4, 6.3) */}
      {hasCta && (
        <a
          href={blok.cta_url}
          className="inline-flex items-center justify-center px-8 py-3.5 rounded-[4px] bg-secondary-ice-blue text-brand-deep text-button-large font-semibold drop-shadow-[0_4px_8px_rgba(91,184,245,0.32)] transition-colors hover:bg-secondary-light-sky focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-white focus-visible:ring-offset-2 focus-visible:ring-offset-brand-blue"
        >
          {blok.cta_label}
        </a>
      )}
    </div>
  );
};

export const Hero: React.FC<HeroProps> = ({ blok }) => {
  const variant: HeroVariant = blok.variant === 'secondary' ? 'secondary' : 'default';
  const isSecondary = variant === 'secondary';

  // Secondary variant: no min-height, content drives height
  // Default variant: desktop has a fixed min-height for the absolutely-positioned
  // stat cards; mobile lets content flow naturally (stat cards sit below the CTA).
  const sectionClasses = isSecondary
    ? 'relative overflow-hidden'
    : 'relative overflow-hidden desktop:min-h-[900px]';

  return (
    <section {...storyblokEditable(blok as unknown as SbBlokData)} className={sectionClasses}>
      {/* Background - different treatment per variant */}
      {isSecondary ? <HeroBackgroundSecondary /> : <HeroBackground />}

      {/* Content container */}
      <SiteContainer className="relative z-10">
        <HeroContent blok={blok} variant={variant} />
      </SiteContainer>

      {/* Stat cards section - only shown for default variant */}
      {!isSecondary && (
        <StatCards
          showStats={blok.show_stats}
          stat1Value={blok.stat_1_value}
          stat1Label={blok.stat_1_label}
          stat2Value={blok.stat_2_value}
          stat2Label={blok.stat_2_label}
          stat3Value={blok.stat_3_value}
          stat3Label={blok.stat_3_label}
        />
      )}
    </section>
  );
};
